import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import FilterPanel from '@/pages/category/components/FilterPanel';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  filterMovies,
  getAllGenres,
  getAllRegions,
  getAllYears,
  paginate,
  totalPages,
  type SortKey,
} from '@/utils/media';
import type { MediaCategory } from '@/types/media';

const PAGE_SIZE = 20;

const SORT_OPTIONS: { key: SortKey; label: string; icon: string }[] = [
  { key: 'updated', label: '更新时间', icon: 'ri-time-line' },
  { key: 'newest', label: '新上架', icon: 'ri-add-circle-line' },
  { key: 'rating', label: '评分', icon: 'ri-star-line' },
  { key: 'popular', label: '热度', icon: 'ri-fire-line' },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const type = (searchParams.get('type') === 'tv' ? 'tv' : 'movie') as MediaCategory;

  const [genres, setGenres] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const isMovie = type === 'movie';

  usePageMeta(
    isMovie ? '电影分类 - 嫩草影院' : '电视剧分类 - 嫩草影院',
    isMovie
      ? '浏览嫩草影院最新电影资源分类，发现丰富多样的影片。'
      : '浏览嫩草影院最新电视剧资源分类，热播剧集持续更新。',
    isMovie ? '电影分类,最新电影,嫩草影院' : '电视剧分类,热播剧,嫩草影院',
  );

  useEffect(() => {
    setGenres([]);
    setYears([]);
    setRegions([]);
    setPage(1);
  }, [type]);

  const genreOptions = useMemo(() => getAllGenres(), []);
  const yearOptions = useMemo(() => getAllYears(), []);
  const regionOptions = useMemo(() => getAllRegions(), []);

  const result = useMemo(
    () =>
      filterMovies({
        category: type,
        genres,
        years: years.map(Number),
        regions,
        sort,
      }),
    [type, genres, years, regions, sort],
  );

  const pages = totalPages(result.length, PAGE_SIZE);
  const pageItems = paginate(result, page, PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [genres, years, regions, sort]);

  const toggle = (value: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const clearAll = () => {
    setGenres([]);
    setYears([]);
    setRegions([]);
  };

  const hasFilter = genres.length > 0 || years.length > 0 || regions.length > 0;

  const switchType = (next: MediaCategory) => {
    const params = new URLSearchParams(searchParams);
    params.set('type', next);
    setSearchParams(params);
  };

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <Navbar />

      <main className="flex-1">
        {/* 头部 */}
        <section className="border-b border-background-800 bg-background-900/50">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span>分类</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-300">{isMovie ? '电影' : '电视剧'}</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-50 md:text-3xl">
              {isMovie ? '电影分类' : '电视剧分类'}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-400">
              {isMovie
                ? '浏览嫩草影院最新电影资源分类，涵盖科幻、悬疑、动作、爱情等多元题材，发现丰富多样的影片。'
                : '浏览嫩草影院最新电视剧资源分类，热播剧集、悬疑探案、古装权谋持续更新，追剧不掉队。'}
            </p>

            <div className="mt-6 inline-flex items-center gap-1 rounded-full bg-background-800 p-1">
              <button
                type="button"
                onClick={() => switchType('movie')}
                className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-5 text-sm font-medium transition-colors ${
                  isMovie ? 'bg-primary-500 text-background-950' : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                <i className="ri-film-line" />
                电影
              </button>
              <button
                type="button"
                onClick={() => switchType('tv')}
                className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-5 text-sm font-medium transition-colors ${
                  !isMovie ? 'bg-primary-500 text-background-950' : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                <i className="ri-tv-2-line" />
                电视剧
              </button>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full px-4 py-8 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
            {/* 侧栏筛选 - 桌面 */}
            <aside className="hidden lg:block">
              <div className="sticky top-20 rounded-lg border border-background-800 bg-background-900 p-5">
                <FilterPanel
                  genres={genreOptions}
                  years={yearOptions}
                  regions={regionOptions}
                  selectedGenres={genres}
                  selectedYears={years}
                  selectedRegions={regions}
                  onToggleGenre={(v) => toggle(v, genres, setGenres)}
                  onToggleYear={(v) => toggle(v, years, setYears)}
                  onToggleRegion={(v) => toggle(v, regions, setRegions)}
                  onClearAll={clearAll}
                />
              </div>
            </aside>

            {/* 内容 */}
            <div>
              {/* 排序栏 */}
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 text-sm text-foreground-200 transition-colors hover:border-primary-600 lg:hidden"
                  >
                    <i className="ri-filter-3-line" />
                    筛选
                    {hasFilter && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs font-bold text-background-950">
                        {genres.length + years.length + regions.length}
                      </span>
                    )}
                  </button>
                  <p className="text-sm text-foreground-400">
                    共 <span className="font-bold text-primary-400">{result.length}</span> 部资源
                  </p>
                </div>

                <div className="flex items-center gap-1 overflow-x-auto rounded-full bg-background-800 p-1 hide-scrollbar">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSort(opt.key)}
                      className={`flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-4 text-sm transition-colors ${
                        sort === opt.key
                          ? 'bg-primary-500 font-medium text-background-950'
                          : 'text-foreground-300 hover:text-foreground-50'
                      }`}
                    >
                      <i className={opt.icon} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {pageItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {pageItems.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} showSummary />
                    ))}
                  </div>
                  <div className="mt-10">
                    <Pagination current={page} total={pages} onChange={handlePageChange} />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-background-800 bg-background-900 px-6 py-20 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
                    <i className="ri-emotion-sad-line text-3xl" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-100">
                    未找到匹配影视
                  </h3>
                  <p className="mt-2 text-sm text-foreground-400">
                    当前筛选条件下没有结果，建议重置筛选后再试。
                  </p>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* 移动端筛选抽屉 */}
      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-xl bg-background-900 p-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-50">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-300 hover:bg-background-800"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel
              genres={genreOptions}
              years={yearOptions}
              regions={regionOptions}
              selectedGenres={genres}
              selectedYears={years}
              selectedRegions={regions}
              onToggleGenre={(v) => toggle(v, genres, setGenres)}
              onToggleYear={(v) => toggle(v, years, setYears)}
              onToggleRegion={(v) => toggle(v, regions, setRegions)}
              onClearAll={clearAll}
            />
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={clearAll}
                className="flex h-12 flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-700 text-sm font-medium text-foreground-200"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex h-12 flex-1 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-950"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}